import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TagManager from 'react-gtm-module';

import queryString from 'query-string';
import Button from '@material-ui/core/Button';
import React, { useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import withWidth from '@material-ui/core/withWidth';
import Typography from '@material-ui/core/Typography';
import { withStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { constructGTM } from '../../utilities/helpers';
import { loginThemeExport } from '../../themes/loginTheme';
import {
  selectLoginLoadingStatus,
  selectLoginError,
  selectLoginScreen,
  selectMessage,
  selectIsAuthenticated,
} from '../../store/selectors';
import { email } from '../../utilities/validators';
import { addNotification } from '../../store/actions/notification';
import accountVerificationErrCodeMsgMapping from './accountVerificationErrCodeMsgMapping';

/* Styles */
import styles from './style';

/* Logos */
import JHLogo from '../../icons/JHLogo';
// import bgImg from './images/loginBackground.jpg';
import bgImg from './images/MyHardieLoginImage.jpg';

import {
  loginAction,
  forgotPwAction,
  changeLoginScreenAction,
  verificationCodeAction,
  newPwAction,
  resendVerificatonEmail,
} from '../../store/actions/login';
import LoginForm from './components/LoginForm';
import ForgotPwForm from './components/ForgotPwForm';
import VerificationForm from './components/VerificationForm';
import NewPwForm from './components/NewPwForm';
import ResendVerificationForm from './components/ResendVerificationEmailForm';
import { openModal } from '../../store/actions/modal';
import {
  REGISTRATION_MODAL_REFERRER,
  NOTIFICATON_LEVELS,
  LOGIN_SCREENS,
} from '../../constants';
import RegistrationModal from '../RegistrationModal';
import ModalController from '../ModalController';
import Footer from '../Footer';

const propTypes = {
  isLoginLoading: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  loginError: PropTypes.object,
  loginScreen: PropTypes.string.isRequired,
  message: PropTypes.string,
  classes: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
  onChangeLoginScreen: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
  }).isRequired,
  onAddNotification: PropTypes.func.isRequired,
};

const submitNormal = async (values, dispatch, history) => {
  TagManager.initialize(
    constructGTM({
      dataLayer: {
        event: 'login_myhardies',
        user_id: values.email,
      },
    }),
  );
  dispatch(loginAction(values, history));
};

const submitForgotPw = async (values, dispatch, history) => {
  dispatch(forgotPwAction(values, history));
};

const submitVerification = async (values, dispatch, history) => {
  dispatch(verificationCodeAction(values, history));
};

const submitNewPw = async (values, dispatch, history) => {
  dispatch(newPwAction(values, history));
};

const submitResendVerificationEmailForm = (values, dispatch) => {
  dispatch(resendVerificatonEmail(values.email));
};

export const LoginPage = ({
  isLoginLoading,
  loginError,
  loginScreen,
  message,
  classes,
  width,
  onChangeLoginScreen,
  onOpenModal,
  location,
  onAddNotification,
  isAuthenticated,
}) => {
  const [
    preFilledEmailForPasswordReset,
    setPreFilledEmailForPasswordReset,
  ] = useState('');

  useEffect(() => {
    // If the user lands on the login page by clicking on registration verification link in the email,
    // we need to display corresponding message to indicate if email
    // verification is successful or not
    const query = queryString.parse(location.search);

    if (query.referrer === 'signUpConfirmation') {
      if (query.status === 'succeeded') {
        onAddNotification(
          'Your email is now verified.',
          NOTIFICATON_LEVELS.SUCCESS,
        );
        return;
      }

      if (query.status === 'failed') {
        const errMsg = accountVerificationErrCodeMsgMapping(query.errCode);

        if (query.errCode === 'ExpiredCodeException') {
          onChangeLoginScreen(LOGIN_SCREENS.RESEND_VERIFICATION_EMAIL);
        }

        onAddNotification(errMsg, NOTIFICATON_LEVELS.ERROR, true);
      }
    }

    if (query.referrer === 'builderImportResetPassword') {
      onChangeLoginScreen(LOGIN_SCREENS.FORGOT_PASSWORD);

      if (
        query.preFilledEmailForPasswordReset &&
        email(query.preFilledEmailForPasswordReset) === undefined
      ) {
        setPreFilledEmailForPasswordReset(query.preFilledEmailForPasswordReset);
      }
    }
    console.log('Login Page', isAuthenticated);

    if (localStorage.getItem('accessToken')) {
      window.location = '/';
    }
  }, []);

  const handleForgotPw = () => {
    onChangeLoginScreen(LOGIN_SCREENS.FORGOT_PASSWORD);
  };
  const handleBackToNormalForm = () => {
    onChangeLoginScreen(LOGIN_SCREENS.NORMAL);
  };
  const handleTryAgain = () => {
    onChangeLoginScreen(LOGIN_SCREENS.VERIFICATION);
  };

  let formToDisplay;
  if (loginScreen === LOGIN_SCREENS.VERIFICATION) {
    formToDisplay = (
      <VerificationForm
        onSubmit={submitVerification}
        isLoginLoading={isLoginLoading}
        message={message}
        loginError={loginError}
        handleBackToNormalForm={handleBackToNormalForm}
      />
    );
  } else if (loginScreen === LOGIN_SCREENS.NORMAL) {
    formToDisplay = (
      <LoginForm
        onSubmit={submitNormal}
        isLoginLoading={isLoginLoading}
        loginError={loginError}
        handleForgotPw={handleForgotPw}
      />
    );
  } else if (loginScreen === LOGIN_SCREENS.FORGOT_PASSWORD) {
    const initialValues = {
      email: preFilledEmailForPasswordReset,
    };

    formToDisplay = (
      <ForgotPwForm
        onSubmit={submitForgotPw}
        isLoginLoading={isLoginLoading}
        loginError={loginError}
        handleBackToNormalForm={handleBackToNormalForm}
        initialValues={initialValues}
      />
    );
  } else if (loginScreen === LOGIN_SCREENS.NEW_PASSWORD) {
    formToDisplay = (
      <NewPwForm
        onSubmit={submitNewPw}
        handleTryAgain={handleTryAgain}
        isLoginLoading={isLoginLoading}
        loginError={loginError}
        handleBackToNormalForm={handleBackToNormalForm}
      />
    );
  } else if (loginScreen === LOGIN_SCREENS.NEW_PASSWORD_SUCCESS) {
    formToDisplay = (
      <>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.newPwSuccessText}
        >
          Password successfully reset. Please login.
        </Typography>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleBackToNormalForm}
        >
          Return to Login
        </Button>
      </>
    );
  } else if (loginScreen === LOGIN_SCREENS.RESEND_VERIFICATION_EMAIL) {
    formToDisplay = (
      <ResendVerificationForm
        onSubmit={submitResendVerificationEmailForm}
        isLoginLoading={isLoginLoading}
        loginError={loginError}
        handleBackToNormalForm={handleBackToNormalForm}
      />
    );
  }

  const handleOpenModal = evt => {
    evt.preventDefault();
    // Log event to GTM
    TagManager.initialize(
      constructGTM({
        dataLayer: {
          event: 'Registrations - Clicked Apply',
        },
      }),
    );
    onOpenModal('styledRegistrationModal', {
      // There are 2 ways to open the registration modal
      // 1. By clicking on the registration link (referrer: link)
      // 2. When login fails (referrer: login-failure)
      referrer: REGISTRATION_MODAL_REFERRER.LINK, // here we want to open the modal when the user clicks on the registration link
    });
  };
  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <MuiThemeProvider theme={loginThemeExport}>
          {width !== 'xs' && (
            <div
              className={classes.loginLeftPanel}
              style={{ backgroundImage: `url(${bgImg})` }}
            >
              {/* <h1>myHardies Login</h1> */}
              <div>
                <div className={classes.whiteBox}>
                  <Typography variant="h1" className={classes.headerContent}>
                    Welcome to <span>myHardies.</span>
                  </Typography>
                  <Typography variant="body1" className={classes.textContent}>
                    Here you can access the complete range of James Hardie
                    technical documentation. BIM files, tools and estimators, as
                    well as bespoke project support to help you get the job done
                  </Typography>
                  <Typography variant="body1" className={classes.textContent}>
                    And if you’re a builder, you get the added benefit of being
                    able to connect with customers all in the one place. Get
                    started now.
                  </Typography>
                </div>
              </div>
            </div>
          )}
          <div className={classes.loginRightPanel}>
            <div className={classes.loginItemsContain}>
              <div className={classes.JhIconContain}>
                <JHLogo />
              </div>
              <div className={classes.form}>{formToDisplay}</div>
              {loginScreen !== LOGIN_SCREENS.RESEND_VERIFICATION_EMAIL && (
                <div className={classes.signUpButtonContainer}>
                  <Typography
                    // color="textSecondary"
                    variant="body2"
                    className={classes.troubleLoginText}
                  >
                    Don&rsquo;t have an account?
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleOpenModal}
                    className={classes.signUpButton}
                  >
                    Apply for a myHardies account
                  </Button>
                </div>
              )}
            </div>
            <Footer classes={{ root: classes.footer }} />
          </div>
        </MuiThemeProvider>
        <ModalController>
          <RegistrationModal />
        </ModalController>
      </div>
    </div>
  );
};

LoginPage.propTypes = propTypes;

// this is how you can use selectors
const mapStateToProps = createStructuredSelector({
  isLoginLoading: selectLoginLoadingStatus(),
  loginError: selectLoginError(),
  loginScreen: selectLoginScreen(),
  message: selectMessage(),
  isAuthenticated: selectIsAuthenticated(),
});

const mapDispatchToProps = dispatch => ({
  // eslint-disable-next-line prettier/prettier
  onChangeLoginScreen: (newScreen) =>
    dispatch(changeLoginScreenAction(newScreen)),
  onOpenModal: (name, meta) => dispatch(openModal(name, meta)),
  // eslint-disable-next-line prettier/prettier
  onAddNotification: (message, level, autoHideDuration) =>
    dispatch(addNotification(message, level, autoHideDuration)),
});

export default withStyles(styles)(
  withWidth()(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(LoginPage),
  ),
);
